import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

let nextRowKey = 1;
const withKey = (row) => ({ ...row, _key: nextRowKey++ });

// Wandelt die Details aus der API ([{merkmal_id, wert, ...}]) in Editor-Zeilen um.
export const toEditorRows = (details) =>
  Array.isArray(details)
    ? details.map((d) => withKey({ merkmal_id: d.merkmal_id, wert: d.wert }))
    : [];

// Payload für das Backend: nur merkmal_id + wert, Reihenfolge = Array-Reihenfolge.
export const toDetailsPayload = (rows) =>
  rows.map(({ merkmal_id, wert }) => ({
    merkmal_id: Number(merkmal_id),
    wert: String(wert ?? "").trim(),
  }));

export default function DetailsEditor({ rows, onChange }) {
  const [merkmale, setMerkmale] = useState([]);
  const [showManage, setShowManage] = useState(false);
  const [newMerkmal, setNewMerkmal] = useState({ label: "", einheit: "" });
  // Ungespeicherte Änderungen in "Merkmale verwalten": merkmal_id -> { label, einheit }
  const [edits, setEdits] = useState({});

  useEffect(() => {
    const ac = new AbortController();

    async function loadMerkmale() {
      try {
        const res = await fetch(
          `${process.env.BACKEND_URL}/api/admin/merkmale`,
          { credentials: "include", signal: ac.signal },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setMerkmale(json.data ?? []);
      } catch (e) {
        if (e.name !== "AbortError")
          toast.error("Merkmale konnten nicht geladen werden");
      }
    }

    loadMerkmale();
    return () => ac.abort();
  }, []);

  const usedIds = new Set(rows.map((r) => Number(r.merkmal_id)));
  const merkmalById = new Map(merkmale.map((m) => [m.merkmal_id, m]));

  const updateRow = (index, patch) =>
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const removeRow = (index) => onChange(rows.filter((_, i) => i !== index));

  const moveRow = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const addRow = () =>
    onChange([...rows, withKey({ merkmal_id: "", wert: "" })]);

  const createMerkmal = async () => {
    const label = newMerkmal.label.trim();
    if (!label) {
      toast.error("Bitte eine Bezeichnung eingeben");
      return;
    }

    try {
      const res = await fetch(`${process.env.BACKEND_URL}/api/admin/merkmale`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label, einheit: newMerkmal.einheit.trim() }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);

      setMerkmale((prev) => [...prev, { ...json.data, anzahl_produkte: 0 }]);
      onChange([
        ...rows,
        withKey({ merkmal_id: json.data.merkmal_id, wert: "" }),
      ]);
      setNewMerkmal({ label: "", einheit: "" });
      toast.success(`Merkmal "${label}" angelegt`);
    } catch (e) {
      toast.error(e.message || "Merkmal konnte nicht angelegt werden");
    }
  };

  const editValue = (m, field) =>
    edits[m.merkmal_id]?.[field] ?? m[field] ?? "";

  const setEdit = (m, field, value) =>
    setEdits((prev) => ({
      ...prev,
      [m.merkmal_id]: {
        label: editValue(m, "label"),
        einheit: editValue(m, "einheit"),
        ...prev[m.merkmal_id],
        [field]: value,
      },
    }));

  const saveMerkmal = async (m) => {
    const label = editValue(m, "label").trim();
    const einheit = editValue(m, "einheit").trim();
    if (!label) {
      toast.error("Bezeichnung darf nicht leer sein");
      return;
    }

    try {
      const res = await fetch(
        `${process.env.BACKEND_URL}/api/admin/merkmale/${m.merkmal_id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ label, einheit, sort_order: m.sort_order }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);

      setMerkmale((prev) =>
        prev.map((x) =>
          x.merkmal_id === m.merkmal_id
            ? { ...x, label, einheit: einheit || null }
            : x,
        ),
      );
      setEdits(({ [m.merkmal_id]: _saved, ...rest }) => rest);
      toast.success(`Merkmal "${label}" gespeichert`);
    } catch (e) {
      toast.error(e.message || "Merkmal konnte nicht gespeichert werden");
    }
  };

  const deleteMerkmal = async (m) => {
    if (!window.confirm(`Merkmal "${m.label}" endgültig löschen?`)) return;

    try {
      const res = await fetch(
        `${process.env.BACKEND_URL}/api/admin/merkmale/${m.merkmal_id}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.message || `HTTP ${res.status}`);
      }

      setMerkmale((prev) => prev.filter((x) => x.merkmal_id !== m.merkmal_id));
      // Noch nicht gespeicherte Zeilen mit diesem Merkmal entfernen,
      // sonst schlägt das Speichern des Produkts fehl.
      onChange(rows.filter((r) => Number(r.merkmal_id) !== m.merkmal_id));
      toast.success(`Merkmal "${m.label}" gelöscht`);
    } catch (e) {
      toast.error(e.message || "Merkmal konnte nicht gelöscht werden");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="font-medium">Produkt-Details</span>

      {rows.length === 0 && (
        <p className="text-sm text-gray-500">Keine Details hinterlegt.</p>
      )}

      {rows.map((row, index) => {
        const einheit = merkmalById.get(Number(row.merkmal_id))?.einheit;
        return (
          <div key={row._key} className="flex items-center gap-2">
            <select
              className="border rounded px-2 py-2 bg-white w-48 shrink-0"
              value={row.merkmal_id}
              onChange={(e) =>
                updateRow(index, { merkmal_id: Number(e.target.value) })
              }
            >
              <option value="" disabled>
                Merkmal wählen
              </option>
              {merkmale.map((m) => (
                <option
                  key={m.merkmal_id}
                  value={m.merkmal_id}
                  disabled={
                    usedIds.has(m.merkmal_id) &&
                    m.merkmal_id !== Number(row.merkmal_id)
                  }
                >
                  {m.label}
                  {m.einheit ? ` (${m.einheit})` : ""}
                </option>
              ))}
            </select>

            <div className="flex flex-1 items-center border rounded">
              <input
                className="flex-1 min-w-0 px-3 py-2 rounded"
                value={row.wert}
                placeholder="Wert"
                onChange={(e) => updateRow(index, { wert: e.target.value })}
              />
              {einheit && (
                <span className="px-2 text-sm text-gray-500">{einheit}</span>
              )}
            </div>

            <button
              type="button"
              className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
              onClick={() => moveRow(index, -1)}
              disabled={index === 0}
              aria-label="Nach oben"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
              onClick={() => moveRow(index, 1)}
              disabled={index === rows.length - 1}
              aria-label="Nach unten"
            >
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="p-1 text-red-500 hover:text-red-700"
              onClick={() => removeRow(index)}
              aria-label="Entfernen"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      })}

      <div className="flex gap-3 mt-1">
        <button
          type="button"
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          onClick={addRow}
        >
          <Plus className="w-4 h-4" /> Merkmal hinzufügen
        </button>
        <button
          type="button"
          className="text-sm text-gray-600 hover:text-gray-900"
          onClick={() => setShowManage((v) => !v)}
        >
          {showManage ? "Merkmale verwalten schließen" : "Merkmale verwalten"}
        </button>
      </div>

      {showManage && (
        <div className="flex flex-col gap-2 border rounded p-3 bg-gray-50">
          <p className="text-xs text-gray-500">
            Änderungen an Bezeichnung oder Einheit gelten für alle Produkte. Ein
            Merkmal kann erst gelöscht werden, wenn es bei keinem Produkt mehr
            verwendet wird.
          </p>

          {merkmale.map((m) => {
            const changed = Boolean(edits[m.merkmal_id]);
            const used = Number(m.anzahl_produkte) > 0;
            return (
              <div key={m.merkmal_id} className="flex items-center gap-2">
                <input
                  className="border rounded px-3 py-1.5 flex-1 min-w-0 bg-white"
                  value={editValue(m, "label")}
                  onChange={(e) => setEdit(m, "label", e.target.value)}
                />
                <input
                  className="border rounded px-3 py-1.5 w-20 bg-white"
                  placeholder="Einheit"
                  value={editValue(m, "einheit")}
                  onChange={(e) => setEdit(m, "einheit", e.target.value)}
                />
                <span className="text-xs text-gray-500 w-24 shrink-0">
                  {used
                    ? `${m.anzahl_produkte} Produkt${m.anzahl_produkte == 1 ? "" : "e"}`
                    : "nicht verwendet"}
                </span>
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-30 w-16"
                  onClick={() => saveMerkmal(m)}
                  disabled={!changed}
                >
                  Speichern
                </button>
                <button
                  type="button"
                  className="p-1 text-red-500 hover:text-red-700 disabled:opacity-30"
                  onClick={() => deleteMerkmal(m)}
                  disabled={used}
                  title={
                    used
                      ? "Wird noch bei Produkten verwendet"
                      : "Merkmal löschen"
                  }
                  aria-label="Merkmal löschen"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}

          <span className="text-sm font-medium mt-2">Neues Merkmal</span>
          <div className="flex items-center gap-2">
            <input
              className="border rounded px-3 py-2 flex-1 min-w-0"
              placeholder="Bezeichnung, z.B. Breite"
              value={newMerkmal.label}
              onChange={(e) =>
                setNewMerkmal({ ...newMerkmal, label: e.target.value })
              }
            />
            <input
              className="border rounded px-3 py-2 w-24"
              placeholder="Einheit"
              value={newMerkmal.einheit}
              onChange={(e) =>
                setNewMerkmal({ ...newMerkmal, einheit: e.target.value })
              }
            />
            <button
              type="button"
              className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700"
              onClick={createMerkmal}
            >
              Anlegen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
